import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledRecommendationsSection = styled.section`
  max-width: 1100px;

  header {
    ${({ theme }) => theme.mixins.flexBetween};
    gap: 20px;
    align-items: flex-start;
    flex-wrap: wrap;

    .header-copy {
      min-width: min(520px, 100%);
    }

    .subheading {
      margin: 8px 0 0;
      max-width: 60ch;
      color: var(--slate);
      font-size: var(--fz-lg);
      line-height: 1.5;
    }

    .controls {
      display: none;
      gap: 10px;
      align-self: center;

      @media (min-width: 768px) {
        display: flex;
      }
    }

    .control-btn {
      ${({ theme }) => theme.mixins.smallButton};
      padding: 10px 14px;
    }
  }

  .carousel {
    display: flex;
    gap: 15px;
    margin-top: 40px;
    padding: 10px 5px 20px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    scroll-padding-left: 5px;
    -webkit-overflow-scrolling: touch;

    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  @media (max-width: 768px) {
    .carousel {
      align-items: flex-start;
    }

    .carousel > * {
      align-self: flex-start;
    }
  }

  .hint {
    margin-top: 12px;
    color: var(--slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
  }
`;

const StyledCard = styled.blockquote`
  ${({ theme }) => theme.mixins.boxShadow};
  flex: 0 0 clamp(280px, 55vw, 420px);
  scroll-snap-align: start;
  background-color: var(--light-navy);
  border-radius: var(--border-radius);
  padding: 1.75rem;
  margin: 0;
  position: relative;
  overflow: hidden;

  .quote {
    color: var(--light-slate);
    font-size: 17px;
    line-height: 1.6;
  }

  footer {
    margin-top: 15px;
    color: var(--lightest-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
  }

  .role {
    color: var(--slate);
    display: block;
    margin-top: 4px;
  }
`;

const Recommendations = () => {
  const data = useStaticQuery(graphql`
    query {
      recommendations: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/user/recommendations/" } }
        sort: { fields: [frontmatter___order, frontmatter___name], order: ASC }
      ) {
        edges {
          node {
            frontmatter {
              name
              role
              order
            }
            html
          }
        }
      }
    }
  `);

  const recommendations = useMemo(() => data.recommendations.edges.filter(({ node }) => node), [
    data.recommendations.edges,
  ]);
  if (!recommendations || recommendations.length === 0) {
    return null;
  }

  const revealContainer = useRef(null);
  const carouselRef = useRef(null);
  const cardElsRef = useRef([]);
  const [isPaused, setIsPaused] = useState(false);
  const [autoEnabled, setAutoEnabled] = useState(false);
  const activeIndexRef = useRef(0);
  const [hasVisited, setHasVisited] = useState(false);
  const pauseTimeoutRef = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  useEffect(() => {
    if (!carouselRef.current) {
      return;
    }

    const el = carouselRef.current;
    let raf = null;

    const onScroll = () => {
      if (raf) {
        return;
      }
      raf = requestAnimationFrame(() => {
        raf = null;
        let bestIndex = 0;
        let bestDistance = Infinity;
        for (let i = 0; i < cardElsRef.current.length; i++) {
          const card = cardElsRef.current[i];
          if (!card) {
            continue;
          }
          const targetLeft = card.offsetLeft;
          const distance = Math.abs(targetLeft - el.scrollLeft);
          if (distance < bestDistance) {
            bestDistance = distance;
            bestIndex = i;
          }
        }
        if (bestIndex !== activeIndexRef.current) {
          activeIndexRef.current = bestIndex;
        }
      });
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) {
        cancelAnimationFrame(raf);
      }
    };
  }, [recommendations.length]);

  useEffect(() => {
    if (!revealContainer.current) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          setHasVisited(true);
        }
      },
      { threshold: 0.25 },
    );

    observer.observe(revealContainer.current);
    return () => observer.disconnect();
  }, []);

  const isVisibleEnough = () => {
    if (!revealContainer.current || typeof window === 'undefined') {
      return false;
    }
    const rect = revealContainer.current.getBoundingClientRect();
    const viewportH = window.innerHeight || 0;
    if (viewportH === 0) {
      return false;
    }
    const visiblePx = Math.min(rect.bottom, viewportH) - Math.max(rect.top, 0);
    const visibleRatio = visiblePx / Math.max(rect.height, 1);
    return visiblePx > 0 && visibleRatio >= 0.25;
  };

  const scrollToIndex = (index, options = {}) => {
    if (!carouselRef.current) {
      return;
    }
    const maxIndex = Math.max(recommendations.length - 1, 0);
    const nextIndex = ((index % (maxIndex + 1)) + (maxIndex + 1)) % (maxIndex + 1);
    activeIndexRef.current = nextIndex;

    const behavior =
      options.behavior || (prefersReducedMotion ? 'auto' : options.smooth ? 'smooth' : 'auto');
    const preventPageJump = Boolean(options.preventPageJump);
    const scrollYBefore = typeof window !== 'undefined' ? window.scrollY : undefined;
    const scrollXBefore = typeof window !== 'undefined' ? window.scrollX : undefined;

    const target = cardElsRef.current[nextIndex];
    const targetLeft = target?.offsetLeft;
    if (typeof targetLeft === 'number') {
      carouselRef.current.scrollTo({
        left: targetLeft,
        behavior,
      });

      if (preventPageJump && typeof window !== 'undefined') {
        requestAnimationFrame(() => {
          if (typeof scrollYBefore !== 'number' || typeof scrollXBefore !== 'number') {
            return;
          }
          if (isVisibleEnough()) {
            return;
          }
          const delta = Math.abs(window.scrollY - scrollYBefore);
          if (delta > 2) {
            window.scrollTo(scrollXBefore, scrollYBefore);
          }
        });
      }
      return;
    }

    // Fallback (should be rare)
    carouselRef.current.scrollTo({ left: 0, behavior });
  };

  const pause = (durationMs = null) => {
    setIsPaused(true);
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
      pauseTimeoutRef.current = null;
    }
    if (typeof durationMs === 'number') {
      pauseTimeoutRef.current = setTimeout(() => setIsPaused(false), durationMs);
    }
  };

  const resume = () => {
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
      pauseTimeoutRef.current = null;
    }
    setIsPaused(false);
  };

  const enableAuto = () => setAutoEnabled(true);

  useEffect(() => {
    if (prefersReducedMotion || isPaused || !hasVisited || !autoEnabled) {
      return;
    }
    if (!carouselRef.current) {
      return;
    }
    if (recommendations.length < 2) {
      return;
    }

    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      if (!isVisibleEnough()) {
        return;
      }
      scrollToIndex(activeIndexRef.current + 1, { smooth: true, preventPageJump: true });
    }, 4500);

    return () => clearInterval(interval);
  }, [autoEnabled, hasVisited, isPaused, prefersReducedMotion, recommendations.length]);

  const scrollByCards = direction => {
    pause(6000);
    scrollToIndex(activeIndexRef.current + direction, { smooth: true });
  };

  return (
    <StyledRecommendationsSection id="recommendations" ref={revealContainer}>
      <header>
        <div className="header-copy">
          <h2 className="numbered-heading">Trusted by Founders & Teams</h2>
          <p className="subheading">
            Some feedback from founders, clients, and teammates I have had the pleasure of working
            with.
          </p>
        </div>

        <div className="controls" aria-label="Recommendation carousel controls">
          <button className="control-btn" type="button" onClick={() => scrollByCards(-1)}>
            Prev
          </button>
          <button className="control-btn" type="button" onClick={() => scrollByCards(1)}>
            Next
          </button>
        </div>
      </header>

      <div
        className="carousel"
        ref={carouselRef}
        aria-label="Recommendation carousel"
        onMouseEnter={() => {
          enableAuto();
          pause();
        }}
        onMouseLeave={() => resume()}
        onFocusCapture={() => {
          enableAuto();
          pause();
        }}
        onBlurCapture={() => pause(4000)}
        onPointerDown={() => {
          enableAuto();
          pause(8000);
        }}
        onWheel={() => pause(6000)}
        onTouchStart={() => {
          enableAuto();
          pause(8000);
        }}>
        {recommendations.map(({ node }, i) => {
          const { name, role } = node.frontmatter;
          return (
            <StyledCard key={i} ref={el => (cardElsRef.current[i] = el)}>
              <div className="quote" dangerouslySetInnerHTML={{ __html: node.html }} />
              <footer>
                <span>{name}</span>
                {role && <span className="role">{role}</span>}
              </footer>
            </StyledCard>
          );
        })}
      </div>

      <div className="hint">Tip: swipe/scroll horizontally.</div>
    </StyledRecommendationsSection>
  );
};

export default Recommendations;
